import React, { useEffect, useState } from "react";
import moment from "moment";
import { useFlags } from "launchdarkly-react-client-sdk";

import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import Text from "components/common/Text";
import Select from "components/common/Select";
import Button from "components/common/Button";
import CustomDatePicker from "components/common/DatePicker";

import {
  INACTIVE_REASONS,
  MAX_CHARACTERS_MESSAGE,
  TERMINATION_TIME_END_OF_DAY,
  TERMINATION_TIME_IMMEDIATE,
  TERMINATION_TIMES,
  TERMINATION_TYPES,
} from "constants";

import MarkEmployeeAsInactiveSchema from "validations/Admin/MarkEmployeeAsInactiveSchema";
import { markEmployeeAsInactive } from "services/Admin/Dashboard";
import { formatDateAsLocal } from "util/utils";

import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import { Snackbar, SnackbarContent } from "@mui/material";

import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import "./MarkInActiveWithReason.scss";

// Builds a LOCAL-midnight Date from a date-only ("YYYY-MM-DD") or datetime ISO string.
// `new Date(dateOnlyString)` parses as UTC midnight, which drifts from the LOCAL-midnight
// dates produced by CustomDatePicker (date-fns), causing off-by-one comparisons in Yup's
// `.min()` for positive-UTC-offset timezones. Parsing the Y/M/D parts keeps both in sync.
const parseLocalDate = (value) => {
  if (!value) return undefined;
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
};

export default function MarkInActiveWithReason({
  selectedId,
  open,
  setOpen,
  employeeFullName,
  getEmployeeListHandler,
  status,
  scheduledAction,
  startDate,
}) {
  const { futureTerminationUnassignment } = useFlags();

  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(true);

  const isFutureTermination = scheduledAction !== null;
  const isEmployeeInactive = status === "inactive";
  const isEndOfDayTermination = scheduledAction?.data?.terminationTime === TERMINATION_TIME_END_OF_DAY;
  const scheduledTimezoneLabel = scheduledAction?.employeeTimezone?.split("/")[1];
  const scheduledDisplayDate = moment(
    isEndOfDayTermination ? scheduledAction?.data?.endDate : scheduledAction?.scheduledDate
  ).format("Do MMMM YYYY");

  const joiningDate = parseLocalDate(startDate);

  const methods = useForm({
    resolver: yupResolver(MarkEmployeeAsInactiveSchema(futureTerminationUnassignment, joiningDate)),
    mode: "onChange",
    defaultValues: {
      reason: "",
      comments: "",
      endDate: new Date(),
      terminationType: "",
      terminationTime: TERMINATION_TIME_IMMEDIATE,
    },
  });

  const handleClose = () => {
    setOpen(false);
    methods.reset();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const { handleSubmit, watch, formState, setValue } = methods;
  const { isValid } = formState;

  const reason = watch("reason");
  const endDate = watch("endDate");
  const terminationTime = watch("terminationTime");

  useEffect(() => {
    if (reason && !endDate) {
      const todayDateStart = new Date();
      todayDateStart.setHours(0, 0, 0, 0);
      setValue("endDate", todayDateStart);
    }
  }, [reason, endDate, setValue]);

  // The Termination Time choice only exists for a same-day termination.
  const isEndDateToday = endDate ? moment(endDate).isSame(moment(), "day") : false;

  useEffect(() => {
    if (!isEndDateToday && terminationTime !== TERMINATION_TIME_IMMEDIATE) {
      setValue("terminationTime", TERMINATION_TIME_IMMEDIATE);
    }
  }, [isEndDateToday, terminationTime, setValue]);

  const isButtonDisabled = () => {
    if (!isValid || loading) return true;
    return false;
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const {
        endDate: terminationEndDate,
        terminationType,
        terminationTime: selectedTerminationTime,
        ...rest
      } = data;

      const payload = futureTerminationUnassignment
        ? {
            ...rest,
            endDate: terminationEndDate ? formatDateAsLocal(terminationEndDate) : null,
            ...(terminationType ? { terminationType } : {}),
            ...(isEndDateToday ? { terminationTime: selectedTerminationTime } : {}),
          }
        : rest;
      await markEmployeeAsInactive(selectedId, payload);
      setLoading(false);
      setOpen(false);
      setIsSuccess(true);
      setSnackbarOpen(true);
      methods.reset();
      getEmployeeListHandler();
    } catch (error) {
      console.error("Error marking employee as inactive:", error);
      setLoading(false);
      setSnackbarOpen(true);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="mark-inactive-title"
        size="sm"
        fullWidth={false}
        title={
          <>
            <div className="icon-container">
              <WarningAmberOutlinedIcon className="inactive-employee-amber-icon" />
            </div>
            {!isEmployeeInactive && (
              <DialogTitle
                title={isFutureTermination ? "The employee is scheduled to be deactivated." : "Mark as Inactive"}
              />
            )}
          </>
        }
        content={
          isEmployeeInactive ? (
            <div className="employee-already-inactive-wrapper">
              <p className="mark-inactive-message">
                <span>{employeeFullName}</span> is already marked as <span>Inactive</span>.
              </p>
            </div>
          ) : (
            <>
              {isFutureTermination && futureTerminationUnassignment ? (
                <p className="mark-inactive-message">
                  The employee is scheduled to be deactivated{" "}
                  {isEndOfDayTermination ? "at the end of the day on" : "on"}{" "}
                  <span>
                    "{scheduledDisplayDate}
                    {isEndOfDayTermination ? "" : " 12:00AM"} ({scheduledTimezoneLabel} time zone)"
                  </span>
                  . Editing the date and saving will change the deactivation date for the employee.
                </p>
              ) : (
                <p className="mark-inactive-message">
                  Are you sure you want to mark <span>{employeeFullName}</span> as <span>Inactive</span>?
                </p>
              )}
              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="inactive-modal-content">
                    <DialogContent>
                      <Select
                        label="Reason for marking Inactive"
                        options={INACTIVE_REASONS}
                        name="reason"
                        placeholder="Select"
                        data-testid="reason-select"
                      />

                      {reason && futureTerminationUnassignment && (
                        <div className="mt-1">
                          <CustomDatePicker
                            name="endDate"
                            label="End date"
                            placeholder="Select end date"
                            isRequired
                            minDate={joiningDate}
                          />
                        </div>
                      )}

                      {reason && futureTerminationUnassignment && isEndDateToday && (
                        <div className="mt-1">
                          <Select
                            label="Termination Time"
                            options={TERMINATION_TIMES}
                            name="terminationTime"
                            data-testid="termination-time-select"
                          />
                        </div>
                      )}

                      {reason && futureTerminationUnassignment && (
                        <div className="mt-1">
                          <Select
                            label="Type (optional)"
                            options={TERMINATION_TYPES}
                            name="terminationType"
                            placeholder="Select"
                          />
                        </div>
                      )}

                      <div className="mt-1">
                        <Text
                          name="comments"
                          multiline
                          rows={3}
                          label={`Additional comments ${reason === "Other" ? "" : "(optional)"}`}
                          size="medium"
                        />
                      </div>
                      <p className="inactive-modal-max-words">{MAX_CHARACTERS_MESSAGE}</p>
                    </DialogContent>
                  </div>
                </form>
              </FormProvider>
            </>
          )
        }
        actions={
          <DialogActions
            action1={
              <Button
                variant="text"
                label={isEmployeeInactive ? "Close" : "Cancel"}
                size="large"
                onClick={handleClose}
              />
            }
            action2={
              !isEmployeeInactive && (
                <Button onClick={handleSubmit(onSubmit)} label="Save" size="large" isDisabled={isButtonDisabled()} />
              )
            }
          />
        }
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <SnackbarContent
          message={
            <span className="inactive-employee-message">
              {isSuccess ? <DoneIcon className="mark-inactive-done-icon" /> : null}
              {isSuccess ? (
                <p className="custom-snackbar-message">
                  <span>{employeeFullName}</span> has been marked as <span>Inactive</span>
                </p>
              ) : null}
            </span>
          }
          action={<CloseIcon onClick={handleSnackbarClose} />}
          className="custom-snackbar-content"
        />
      </Snackbar>
    </>
  );
}
